import React, { FC, memo, useEffect, useRef } from "react";
import { FormControl, Input } from "@mui/material";
import classNames from "classnames";

import { useI18n } from "../../providers/I18n/I18n";
import {
  DateTimePicker as MuiDateTimePicker,
  LocalizationProvider,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import useTimezone from "../../customHooks/useTimezone";
import { dateTimeFormatNoSecond } from "../../utils/utcConversion";
import "./style.css";
import useStyles from "./styles";
import {
  addHoursWithTimezone,
  addTimeToDate,
  formatDateUtil,
  getCurrentDateWithTimezone,
  isValidDate,
} from "../../utils/dateFnsUtils";

interface IDateTimePicker {
  id: string;
  label?: React.ReactNode;
  value?: string | Date;
  onChange?: Function;
  errorMessage?: string;
  isTouched?: boolean;
  required?: boolean;
  minDateToday?: boolean;
  enableMaxDate?: boolean;
  enabledMaxDays?: number;
  openCalendarControl?: any;
  isOpenCalendar?: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  selectOption?: string;
}

const DateTimePicker: FC<IDateTimePicker> = ({
  id,
  label,
  value,
  onChange,
  errorMessage,
  isTouched,
  required,
  minDateToday,
  enableMaxDate,
  enabledMaxDays,
  openCalendarControl,
  isOpenCalendar,
  setFieldValue,
  selectOption,
}) => {
  const classes = useStyles();
  const dateInputRef = useRef<HTMLInputElement>();
  const { t } = useI18n();
  const { timezone } = useTimezone();

  const handleClose = () => {
    openCalendarControl(false);
  };

  useEffect(() => {
    if (selectOption !== "send_immediately") {
      if (isValidDate(value)) {
        const formattedDateTime = addHoursWithTimezone(value, timezone);
        setFieldValue(id, formattedDateTime);
      } else {
        const newDateTime = getCurrentDateWithTimezone(timezone);
        const formattedDateTime = addTimeToDate(
          newDateTime,
          10,
          "minutes",
          dateTimeFormatNoSecond,
        );
        setFieldValue(id, formattedDateTime);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOption, timezone]);

  const minDateSelection = minDateToday ? { minDate: new Date() } : {};
  const maxDateSelection = enableMaxDate
    ? { maxDate: addTimeToDate(new Date(), enabledMaxDays, "days") as Date }
    : {};

  return (
    <FormControl tabIndex={-1} className={classes.campaignDateTimeContainer}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MuiDateTimePicker
          {...minDateSelection}
          {...maxDateSelection}
          value={new Date(value) || null}
          open={isOpenCalendar}
          onClose={handleClose}
          onChange={(date) => {
            if (isValidDate(date)) {
              let newDate = formatDateUtil(date, dateTimeFormatNoSecond);
              onChange(newDate ? newDate : "");
            }
          }}
          format="yyyy-MM-dd HH:mm"
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
            seconds: renderTimeViewClock,
          }}
          slotProps={{
            textField: {
              hidden: true,
            },
            tabs: {
              hidden: true,
            },
            toolbar: {
              hidden: true,
            },
          }}
          inputRef={dateInputRef}
        />
      </LocalizationProvider>
    </FormControl>
  );
};

export default memo(DateTimePicker);
